<template>
    <div>
        <slot name="toggle" v-bind="state" />

        <ModalMask
            v-if="!state.is('closed')"
            v-bind="maskConfig"
            :mask-class="maskClass + ' ' + fullscreenClass"
            @mounted="mount"
            @click="closeOnBackdropClick"
        >
            <section ref="refModal" class="Modal" :class="[sizeClass, modalClass]" tabindex="-1" role="dialog" aria-modal="true">
                <button v-if="closeButton" ref="modalCloseBtn" type="button" class="Modal__close" aria-label="Close" @click="state.close()">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                        <path
                            d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"
                        />
                    </svg>
                </button>

                <div v-if="hasSlot('header')" class="Modal__header">
                    <slot name="header" v-bind="state" />
                </div>

                <div v-if="hasSlot('body')" class="Modal__body">
                    <slot name="body" v-bind="state" />
                </div>

                <slot v-bind="state" />

                <div v-if="hasSlot('footer')" class="Modal__footer">
                    <slot name="footer" v-bind="state" />
                </div>
            </section>
        </ModalMask>
    </div>
</template>

<style>
.Modal {
    @apply bg-white relative shadow-2xl mx-auto rounded-lg;

    border: 1px solid rgba(0, 0, 0, 0.2);
    background-clip: padding-box;
    outline: 0;
}

.ModalMask.--fullscreen .Modal {
    @apply rounded-none border-none min-h-full;
}

@screen sm {
    .Modal {
        margin: 30px auto;
    }
}

.Modal__close {
    @apply block absolute text-gray-600 text-2xl z-10 fill-current;

    top: 1rem;
    right: 1rem;
}

.Modal__close svg {
    max-width: 22px;
    height: 22px;
}

.Modal__header {
    @apply text-xl text-gray-900 p-6;
}

.Modal__body {
    @apply leading-normal p-6 pt-0;
}

.Modal__footer {
    @apply p-6 pt-0;
}
</style>

<script type="text/babel">
import { $get, unless } from '@makeabledk/vue-ui/support/helpers'
import { ResponsiveValue } from '@makeabledk/vue-ui/support/rxjs'
import { onBeforeUnmount, provide } from 'vue'
import HasEventListeners from '@makeabledk/vue-ui/mixins/has-event-listeners'
import HasSubscriptions from '@makeabledk/vue-ui/mixins/has-subscriptions'
import ModalMask from './ModalMask'
import useModalState from './useModalState'

export let config = {
    fullscreen: false,
}

export default {
    components: {
        ModalMask,
    },

    mixins: [HasEventListeners, HasSubscriptions],

    props: {
        size: {
            default: '3xl',
        },

        closeButton: {
            default: true,
        },

        closeBackdrop: {
            default: true,
        },

        closeEsc: {
            default: true,
        },

        closeEscEvent: {
            default: 'keyup',
        },

        fullscreen: {
            default() {
                return config.fullscreen
            },
        },

        maskClass: {
            default: '',
        },

        maskConfig: {},

        modalClass: {
            default: '',
        },
    },

    emits: ['state', 'maskEl', 'opening', 'open', 'closing', 'closed'],

    setup(props, context) {
        const state = useModalState(context.emit)

        // Open instantly when no toggle slot provided
        unless(context.slots.toggle, state.open)

        // Close before unmounting component
        onBeforeUnmount(state.close)

        provide('state', state)

        context.emit('state', state)

        return { state }
    },

    data() {
        return {
            modalEl: null,
            isFullScreen: null,
        }
    },

    computed: {
        fullscreenClass() {
            return this.isFullScreen ? '--fullscreen' : ''
        },

        sizeClass() {
            // List of possible values (to prevent purge css from removing them):
            // max-w-3xs max-w-2xs max-w-xs max-w-sm max-w-md max-w-lg max-w-xl
            // max-w-2xl max-w-3xl max-w-4xl max-w-5xl max-w-6xl max-w-7xl max-w-8xl max-w-full
            return this.size.startsWith('max-w-') ? this.size : 'max-w-' + this.size
        },
    },

    created() {
        this.subscribe(ResponsiveValue.create(this.fullscreen), (isFullscreen) => (this.isFullScreen = isFullscreen))
    },

    methods: {
        closeOnBackdropClick(event) {
            // Do not close when potentially clicking the scrollbar
            if (window.innerWidth - event.pageX < 15) {
                return
            }

            if (this.closeBackdrop) {
                this.state.close()
            }
        },

        closeOnEsc(maskEl) {
            this.listen(maskEl, this.closeEscEvent, (event) => {
                if (['Esc', 'Escape'].indexOf(event.key) > -1) {
                    this.state.close()
                    event.stopPropagation()
                }
            })
        },

        mount(maskEl) {
            this.$emit('maskEl', maskEl)

            this.modalEl = this.$refs.refModal

            // Prevent close button from being focus immediately
            if (document.activeElement === $get(this.$refs, 'modalCloseBtn')) {
                this.modalEl.focus()
            }

            if (this.closeEsc) {
                this.closeOnEsc(maskEl)
            }
        },

        hasSlot(name) {
            return typeof this.$slots[name] !== 'undefined'
        },
    },
}
</script>
